import { useCallback, useMemo } from 'react';

import { useSelector } from '@xstate/react';
import { type SnapshotFrom } from 'xstate';

import { useGlobalAppState } from '../../../context/GlobalAppState';
import { type cartMachine } from './Cart.machine';
import { getPendingMutationsNames } from './Cart.machine.utils';

import type { CartMutation } from './Cart.machine.types';

// ────────────────────────────────────────────────────────────────────────────────

export const useCartMachine = () => {
  const { cartMachineRef: cartService } = useGlobalAppState();

  const context = useSelector(cartService, getContext);
  const shouldDisableCartControls = useSelector(cartService, checkIfCartControlsDisabled);
  const pendingCartMutations = getPendingMutationsNames(context);
  const cartViewCounts = useSelector(cartService, getCartViewCounts);

  // ─── HELPERS ─────────────────────────────────────────────────────

  /**
   * Tracks pending cart mutation's fetching state.
   *
   * @example
   * const addLineItemResult = await executeCartMutation(addLineItemMutation);
   */
  const executeCartMutation = useCallback<ExecuteCartMutation>(
    async (mutation, mutationName) => {
      cartService.send({ type: 'REGISTER_PENDING_MUTATION', mutationName });
      const result = await mutation();

      cartService.send({ type: 'UNREGISTER_PENDING_MUTATION', mutationName });

      return result;
    },
    [cartService],
  );

  const registerCartView = useCallback(
    (cartId: string) => {
      cartService.send({ type: 'REGISTER_CART_VIEW', cartId });
    },
    [cartService],
  );

  // ─────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      executeCartMutation,
      pendingCartMutations,
      shouldDisableCartControls,
      registerCartView,
      cartViewCounts,
    }),
    [
      executeCartMutation,
      pendingCartMutations,
      shouldDisableCartControls,
      registerCartView,
      cartViewCounts,
    ],
  );
};

// ─── SELECTORS ──────────────────────────────────────────────────────────────────

function getContext(snapshot: CartMachineSnapshot) {
  return snapshot.context;
}

function checkIfCartControlsDisabled(snapshot: CartMachineSnapshot) {
  return snapshot.matches({ controls: 'disabled' });
}

function getCartViewCounts(snapshot: CartMachineSnapshot) {
  return snapshot.context.cartViewCounts;
}

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type CartMachineSnapshot = SnapshotFrom<typeof cartMachine>;

type ExecuteCartMutation = <DataType>(
  mutation: () => Promise<DataType>,
  mutationName: CartMutation,
) => Promise<DataType>;
